import { useTranslation } from 'react-i18next';
import { Column, ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import ArrowDownIconSolid from '@/components/icons/ArrowDownIconSolid';
import EDateText from '@/components/everridge/EDateText';
import { OrderListItemResponseDto } from '@/features/orders/types/orders-dto';
import OrdersStatusDetails from '@/features/orders/components/OrdersStatusDetails';
import { useOrdersFilter } from '@/features/orders/hooks/useOrdersFilter';
import { useUserRole } from '@/features/identity/hooks/useUserRole.ts';
import { Roles } from '@/@types/auth.types.ts';
import CurrencyValue from '@/features/finances/components/CurrencyValue';

const OrdersPageColumns = () => {
	const { t } = useTranslation();

	const { setFilter } = useOrdersFilter();
	const userRole = useUserRole();

	const ownerColumnsFilter = (
		column: ColumnDef<OrderListItemResponseDto>,
	) => {
		if ('accessorKey' in column) return column.accessorKey !== 'authorName';
		return false;
	};

	const handleSort = (
		columnKey: Capitalize<keyof OrderListItemResponseDto>,
		column: Column<OrderListItemResponseDto>,
	) => {
		const newDirection = column.getIsSorted() === 'asc';
		column.toggleSorting(newDirection);

		setFilter({
			orderBy: columnKey,
			orderDirection: newDirection ? 'asc' : 'desc',
		});
	};

	const columnClasses = 'text-sm text-popover-800';

	const adminColumns: ColumnDef<OrderListItemResponseDto>[] = [
		{
			accessorKey: 'documentDate',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('DocumentDate', column)}>
						{t('main.orders.table.header.timestamp')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={columnClasses}>
					<EDateText date={row.getValue('documentDate')} />
				</div>
			),
		},
		{
			accessorKey: 'authorName',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('AuthorName', column)}>
						{t('main.orders.table.header.orderedBy')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={`truncate ${columnClasses}`}>
					<span>{row.getValue('authorName')}</span>
				</div>
			),
		},
		{
			accessorKey: 'apartmentName',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('ApartmentName', column)}>
						{t('main.orders.table.header.apartment')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={`truncate ${columnClasses}`}>
					<span>{row.getValue('apartmentName')}</span>
				</div>
			),
		},
		{
			accessorKey: 'services',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('Services', column)}>
						{t('main.orders.table.header.services')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={`truncate ${columnClasses}`}>
					<span>{row.getValue('services')}</span>
				</div>
			),
		},
		{
			accessorKey: 'totalAmount',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('TotalAmount', column)}>
						{t('main.orders.table.header.total')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={`truncate ${columnClasses}`}>
					<CurrencyValue
                        value={row.getValue('totalAmount')}
                        asset='other'
                    />					
				</div>
			),
		},
		{
			accessorKey: 'status',
			header: ({ column }) => {
				return (
					<Button
						variant='link'
						className={
							'h-fit p-0 flex items-center gap-2.5 text-sm text-popover-1000 font-medium no-underline hover:no-underline'
						}
						onClick={() => handleSort('Status', column)}>
						{t('main.orders.table.header.status')}
						<div
							className={`transition-transform duration-50 ease-in-out ${
								column.getIsSorted() === 'asc'
									? '-rotate-180'
									: 'rotate-0'
							}`}>
							{<ArrowDownIconSolid />}
						</div>
					</Button>
				);
			},
			cell: ({ row }) => (
				<div className={`truncate ${columnClasses}`}>
					<span>
						<OrdersStatusDetails status={row.getValue('status')} />
					</span>
				</div>
			),
		},
	];

	return userRole === Roles.admin
		? adminColumns
		: adminColumns.filter(ownerColumnsFilter);
};

export default OrdersPageColumns;
